import { AUTOMATED_BROWSER_USER_AGENT_PATTERN } from "./browser-automation-policy.generated";

export { AUTOMATED_BROWSER_USER_AGENT_PATTERN } from "./browser-automation-policy.generated";

declare global {
  interface Window {
    /** Set by browser monitors before any application script executes. */
    __ishSyntheticMonitor?: boolean;
  }
}

const AUTOMATED_BROWSER_USER_AGENT_RE = new RegExp(AUTOMATED_BROWSER_USER_AGENT_PATTERN, "i");

export type BrowserAutomationSignal = "navigator_webdriver" | "automated_user_agent";

/**
 * High-confidence automation hints available before React hydrates.
 *
 * `navigator.webdriver` is the browser-owned WebDriver disclosure. The UA
 * matcher covers explicit crawler identities (YandexBot, Googlebot, headless
 * audits, monitors), not old browser versions or inferred network reputation:
 * a genuine seeker on an old browser must remain in the error denominator.
 */
export function browserAutomationSignal(): BrowserAutomationSignal | null {
  if (typeof window === "undefined" || typeof navigator === "undefined") return null;
  if (window.__ishSyntheticMonitor === true) return null;
  if (navigator.webdriver === true) return "navigator_webdriver";
  return AUTOMATED_BROWSER_USER_AGENT_RE.test(navigator.userAgent || "")
    ? "automated_user_agent"
    : null;
}

export function isAutomatedBrowser(): boolean {
  return browserAutomationSignal() !== null;
}

export function isExplicitAutomatedUserAgent(userAgent: string): boolean {
  return AUTOMATED_BROWSER_USER_AGENT_RE.test(userAgent);
}

export function browserTrafficMarker():
  | { traffic_class: "synthetic" }
  | { traffic_class: "automated"; automation_signal: BrowserAutomationSignal }
  | Record<string, never> {
  if (typeof window !== "undefined" && window.__ishSyntheticMonitor === true) {
    return { traffic_class: "synthetic" };
  }
  const signal = browserAutomationSignal();
  return signal ? { traffic_class: "automated", automation_signal: signal } : {};
}

/**
 * Fail-closed analytics marker shared by every browser transport.
 *
 * Keep this independent from analytics-server: tests and small client islands
 * routinely mock a transport, but synthetic exclusion is a browser identity
 * concern that must remain available whichever transport is substituted.
 */
export function syntheticTrafficMarker(): { traffic_class: "synthetic" } | Record<string, never> {
  return typeof window !== "undefined" && window.__ishSyntheticMonitor === true
    ? { traffic_class: "synthetic" }
    : {};
}
